import {useNavigate} from "react-router-dom";
import React, {useEffect, useState} from "react";
import {getUserId, getVenueSession} from "../../requests/AppSession";

import {Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions, Label} from "@headlessui/react";
import {useAuth} from "@workos-inc/authkit-react";
import {findUserTipHistory, getUserVenueMonths} from "../requests/AppRequestCustomerUser";
import {HistorySkeleton} from "./VTSkeletons";
import {ChevronDownIcon} from "@heroicons/react/16/solid";
import {NumericFormat} from "react-number-format";

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

export default function VTUserTipHistory() {
    const {isLoading, getAccessToken} = useAuth();

    let navigate = useNavigate();

    const [userId, setUserId] = React.useState(null);
    const [tipHistory, setTipHistory] = useState(null);

    const [dates, setDates] = useState([]);
    const [datesLoaded, setDatesLoaded] = useState(false);
    const [monthNumber, setMonthNumber] = useState(null);
    const [yearNumber, setYearNumber] = useState(null);

    const [venueId, setVenueId] = React.useState(null);
    const [venueName, setVenueName] = React.useState(null);

    const [errorMessage, setErrorMessage] = React.useState(null);

    const getDatesSuccess = async responseJson => {
        if (
            responseJson
        ) {
            setDates(responseJson);
            setDatesLoaded(true);
            if (responseJson.length > 0) {
                setSelectedDate(responseJson[0]);
                setMonthNumber(responseJson[0].monthNumber);
                setYearNumber(responseJson[0].yearNumber)
            }
        } else {
            setErrorMessage("Error accessing venue dates, please refresh and verify Internet Connection.");
        }
    };

    const getDatesError = forceLogout => {
        setErrorMessage("Error accessing venue dates, please refresh and verify Internet Connection.");
    };

    useEffect(() => {
        getVenueSession().then(venueSession => {
            if (venueSession && venueSession.venueId && venueSession.venueId !== null) {
                setVenueId(venueSession.venueId);
            } else {
                navigate('/login/mobile', {replace: true});
            }
        })

        return function cleanup() {

        };
    }, []);

    useEffect(() => {
        getUserId().then(userIdSession => {
            if (userIdSession) {
                setUserId(userIdSession);
            } else {
                navigate('/login/mobile', {replace: true});
            }
        })

        if (!isLoading) {
            getUserVenueMonths(getDatesSuccess, getDatesError, getAccessToken);
        }

        return function cleanup() {

        };
    }, [isLoading]);

    let groupBy = function (xs, key) {
        return xs.reduce(function (rv, x) {
            (rv[x[key]] = rv[x[key]] || []).push(x);
            return rv;
        }, {});
    };

    const getSuccess = async responseJson => {
        if (
            responseJson
        ) {
            if (responseJson.length > 0) {
                setVenueName(responseJson[0].venueName);
                setTipHistory(groupBy(responseJson, 'createDateOnlyDate'));
            } else {
                // Always store the grouped-object shape so the render (Object.keys) and the empty
                // check are consistent — an empty month is an empty object, not an array.
                setTipHistory({});
            }
        } else {
            setErrorMessage("Error accessing user tip history, please refresh and verify Internet Connection.");
        }
    };

    const getError = forceLogout => {
        setErrorMessage("Error accessing user tip history, please refresh and verify Internet Connection.");
    };

    useEffect(() => {
        if (!isLoading && userId !== null && venueId !== null && monthNumber !== null && yearNumber !== null) {
            // Reset so the spinner shows and the previous month's data doesn't linger on screen
            // while the new month loads.
            setTipHistory(null);
            findUserTipHistory(venueId, yearNumber, monthNumber, getSuccess, getError, getAccessToken);
        }
        return function cleanup() {

        };
    }, [isLoading, userId, venueId, monthNumber, yearNumber]);

    const [query, setQuery] = useState('');
    const [selectedDate, setSelectedDate] = useState(null);

    const filteredDate =
        query === ''
            ? dates
            : dates.filter((date) => {
                return date.name.toLowerCase().includes(query.toLowerCase())
            })

    // Day-group keys sorted most-recent-first, so the newest day shows at the top regardless of
    // the server's response order.
    const sortedDayKeys = tipHistory
        ? Object.keys(tipHistory).sort((a, b) => new Date(b) - new Date(a))
        : [];

    // Whole-month rollups for the summary card.
    const monthTotalVt = tipHistory
        ? Object.values(tipHistory).reduce((sum, dayTips) =>
            sum + dayTips.reduce((daySum, tip) => daySum + tip.tipAmount, 0), 0)
        : 0;
    const monthTipCount = tipHistory
        ? Object.values(tipHistory).reduce((count, dayTips) => count + dayTips.length, 0)
        : 0;

    // Only treat an empty month list as "no history" once the dates fetch has actually
    // completed — otherwise the empty state flashes before data loads.
    const hasNoMonths = datesLoaded && dates.length === 0;

    return (
        <div className="bg-white dark:bg-gray-900">
            <div className="mx-auto max-w-7xl py-16 px-4 sm:px-6 lg:px-8 lg:pb-24">
                <div className="max-w-xl">
                    <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100 sm:text-3xl">Tip
                        History {venueName && <span className="text-xl">@ {venueName}</span>}</h1>
                    {/*<p className="mt-2 text-sm text-gray-500">*/}
                    {/*    Check the status of recent orders, manage returns, and download invoices.*/}
                    {/*</p>*/}
                </div>

                <div className="mt-16">
                    <h2 className="sr-only">Tip Activity</h2>

                    <div className="space-y-8">

                        <Combobox
                            as="div"
                            value={selectedDate}
                            onChange={(value) => {
                                setQuery('')
                                setSelectedDate(value);
                                if (value) {
                                    setMonthNumber(value.monthNumber);
                                    setYearNumber(value.yearNumber);
                                }
                            }}
                        >
                            <Label className="block text-sm/6 font-medium text-gray-900 dark:text-white">Month</Label>
                            <div className="relative mt-2">
                                <ComboboxInput
                                    className="block w-full rounded-md bg-white py-1.5 pr-12 pl-3 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-pink-600 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-white/10 dark:placeholder:text-gray-500 dark:focus:outline-pink-500"
                                    onChange={(event) => setQuery(event.target.value)}
                                    onBlur={() => setQuery('')}
                                    displayValue={(date) => date?.name}
                                />
                                <ComboboxButton
                                    className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-hidden">
                                    <ChevronDownIcon className="size-5 text-gray-400 dark:text-gray-500" aria-hidden="true"/>
                                </ComboboxButton>

                                {filteredDate && filteredDate.length > 0 && (
                                    <ComboboxOptions
                                        transition
                                        className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg outline-1 outline-black/5 data-leave:transition data-leave:duration-100 data-leave:ease-in data-closed:data-leave:opacity-0 sm:text-sm dark:bg-gray-800 dark:shadow-none dark:-outline-offset-1 dark:outline-white/10"
                                    >
                                        {query.length > 0 && (
                                            <ComboboxOption
                                                value={{id: null, name: query}}
                                                className="cursor-default px-3 py-2 text-gray-900 select-none data-focus:bg-pink-600 data-focus:text-white data-focus:outline-hidden dark:text-white dark:data-focus:bg-pink-500"
                                            >
                                                {query}
                                            </ComboboxOption>
                                        )}
                                        {filteredDate?.map((date) => (
                                            <ComboboxOption
                                                key={date.name}
                                                value={date}
                                                className="cursor-default px-3 py-2 text-gray-900 select-none data-focus:bg-pink-600 data-focus:text-white data-focus:outline-hidden dark:text-white dark:data-focus:bg-pink-500"
                                            >
                                                <div className="flex">
                                                    <span className="block truncate">{date.name}</span>
                                                    <span
                                                        className="ml-2 block truncate text-gray-500 in-data-focus:text-white dark:text-gray-400 dark:in-data-focus:text-white">
                                                        {date.username}
                                                    </span>
                                                </div>
                                            </ComboboxOption>
                                        ))}
                                    </ComboboxOptions>
                                )}
                            </div>
                        </Combobox>

                        <div className="space-y-6">
                        {/* Whole-account empty state (no months at all). */}
                        {hasNoMonths &&
                            <div className="rounded-xl bg-gray-50 dark:bg-gray-800 py-10 text-center ring-1 ring-gray-200 dark:ring-gray-700">
                                <p className="text-base font-medium text-gray-900 dark:text-gray-100">No tip history yet</p>
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Your tips will show up here once you start tipping.</p>
                            </div>}

                        {/* Month summary — the headline numbers for the selected month. */}
                        {tipHistory && sortedDayKeys.length > 0 &&
                            <div className="rounded-xl bg-pink-600 px-6 py-5 shadow-sm">
                                <p className="text-sm font-semibold uppercase tracking-wide text-pink-100">
                                    {selectedDate?.name || "This month"}
                                </p>
                                <div className="mt-1 flex items-baseline justify-between">
                                    <span className="text-3xl font-bold text-white">
                                        <NumericFormat value={monthTotalVt} displayType={'text'} thousandSeparator={true}/> VT
                                    </span>
                                    <span className="text-sm font-medium text-pink-100">
                                        {monthTipCount} {monthTipCount === 1 ? "tip" : "tips"}
                                    </span>
                                </div>
                            </div>}

                        {/* Loading + per-month empty states. */}
                        {!tipHistory && !hasNoMonths && <HistorySkeleton/>}
                        {tipHistory && sortedDayKeys.length === 0 && !hasNoMonths &&
                            <h3 className="text-sm text-gray-500 dark:text-gray-400">No tips for this month.</h3>}

                        {/* Day groups, newest first, as cards. */}
                        {tipHistory && sortedDayKeys.map(key =>
                            <div key={key} className="overflow-hidden rounded-xl ring-1 ring-gray-200 dark:ring-gray-700">
                                <div
                                    className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 px-5 py-4">
                                    <div>
                                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Date</p>
                                        <p className="mt-0.5 font-semibold text-gray-900 dark:text-gray-100">
                                            {new Date(key).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Day total</p>
                                        <p className="mt-0.5 font-bold text-pink-600">
                                            <NumericFormat
                                                value={tipHistory[key].reduce((accumulator, currentValue) => accumulator + currentValue.tipAmount, 0)}
                                                displayType={'text'} thousandSeparator={true}/> VT
                                        </p>
                                    </div>
                                </div>

                                <table className="w-full text-gray-500 dark:text-gray-400">
                                    <caption className="sr-only">Tips</caption>
                                    <thead className="text-left text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500">
                                    <tr>
                                        <th scope="col" className="px-5 py-2 font-semibold">Dancer</th>
                                        <th scope="col" className="px-5 py-2 text-right font-semibold">Tip Amount</th>
                                    </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700 text-sm">
                                    {tipHistory[key].map((tip) => (
                                        <tr key={tip.id}>
                                            <td className="px-5 py-3 text-gray-900 dark:text-gray-100">{tip.dancerName}</td>
                                            <td className="px-5 py-3 text-right font-medium text-gray-900 dark:text-gray-100">
                                                <NumericFormat value={tip.tipAmount} displayType={'text'} thousandSeparator={true}/> VT
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
